import { PaymentActions } from '@app/core/actions';
import { Action, createReducer, on } from '@ngrx/store';
import { GetPlanGroupOutput } from '@smash-sdk/billing/ejs/01-2024/types/GetPlanGroup/GetPlanGroup';
import { SubscriptionActions } from '../actions';
import { Plan } from '@app/shared/models/plan';

export const subscriptionFeatureKey = 'subscription';

export interface State {
  signupProcessing: boolean;
  signupSucceed: boolean;
  signupFailed: boolean;
  signupError: string;

  updateAccountProcessing: boolean;
  updateAccountSucceed: boolean;
  updateAccountFailed: boolean;
  updateAccountError: any;

  updateBillingInformationsProcessing: boolean;
  updateBillingInformationsSucceed: boolean;
  updateBillingInformationsFailed: boolean;
  updateBillingInformationsError: any;

  updateSubscriptionProcessing: boolean;
  updateSubscriptionSucceed: boolean;
  updateSubscriptionFailed: boolean;
  updateSubscriptionError: any;

  planGroupLoaded: boolean;
  plans: GetPlanGroupOutput['group']['plans'];
  plansLoading: boolean;
  plansError: any;
  computedPlan: Plan;
  planPriceComputing: boolean;
  planPriceComputingError: any;
  formData: any;
  billingInformationsLoaded: boolean;
  subscriptionLoaded: boolean;
}

export const initialState: State = {
  signupProcessing: false,
  signupSucceed: false,
  signupFailed: false,
  signupError: null,
  updateAccountProcessing: false,
  updateAccountSucceed: false,
  updateAccountFailed: false,
  updateAccountError: null,
  updateBillingInformationsProcessing: false,
  updateBillingInformationsSucceed: false,
  updateBillingInformationsFailed: false,
  updateBillingInformationsError: null,
  updateSubscriptionProcessing: false,
  updateSubscriptionSucceed: false,
  updateSubscriptionFailed: false,
  updateSubscriptionError: null,
  planGroupLoaded: false,
  plans: [],
  plansLoading: false,
  plansError: null,
  computedPlan: null,
  planPriceComputing: false,
  planPriceComputingError: null,
  formData: null,
  billingInformationsLoaded: false,
  subscriptionLoaded: false,
};

const signupReducer = createReducer(
  initialState,
  // Signup
  on(SubscriptionActions.signup, (state) => ({
    ...state,
    signupProcessing: true,
    signupSucceed: initialState.signupSucceed,
    signupFailed: initialState.signupFailed,
    signupError: initialState.signupError,
    updateBillingInformationsFailed: initialState.updateBillingInformationsFailed,
    updateBillingInformationsError: initialState.updateBillingInformationsError,
  })),
  on(SubscriptionActions.signupSuccess, (state) => ({
    ...state,
    signupProcessing: false,
    signupSucceed: true,
  })),
  on(SubscriptionActions.signupFailure, 
    PaymentActions.processStripePaymentFailure,
    PaymentActions.processPaypalPaymentFailure, (state, { error }) => ({
    ...state,
    signupProcessing: false,
    signupFailed: true,
    signupError: error,
  })),
  // Update Account
  on(SubscriptionActions.updateAccount, (state) => ({
    ...state,
    updateAccountProcessing: true,
    updateAccountSucceed: initialState.updateAccountSucceed,
    updateAccountFailed: initialState.updateAccountFailed,
    updateAccountError: initialState.updateAccountError,
  })),
  on(SubscriptionActions.updateAccountSuccess, (state) => ({
    ...state,
    updateAccountProcessing: false,
    updateAccountSucceed: true,
  })),
  on(SubscriptionActions.updateAccountFailure, (state, { error }) => ({
    ...state,
    signupProcessing: false,
    updateAccountProcessing: false,
    updateAccountFailed: true,
    updateAccountError: error,
    signupError: error,
  })),
  // Update billing informations
  on(SubscriptionActions.updateBillingInformations, (state) => ({
    ...state,
    updateBillingInformationsProcessing: true,
    updateBillingInformationsSucceed: initialState.updateBillingInformationsSucceed,
    updateBillingInformationsFailed: initialState.updateBillingInformationsFailed,
    updateBillingInformationsError: initialState.updateBillingInformationsError,
  })),
  on(SubscriptionActions.updateBillingInformationsSuccess, (state) => ({
    ...state,
    updateBillingInformationsProcessing: false,
    updateBillingInformationsSucceed: true,
  })),
  on(SubscriptionActions.updateBillingInformationsFailure, (state, { error }) => ({
    ...state,
    signupProcessing: false,
    updateBillingInformationsProcessing: false,
    updateBillingInformationsFailed: true,
    updateBillingInformationsError: error,
    signupError: error,
  })),
  // Update subscription
  on(SubscriptionActions.updateSubscription, (state) => ({
    ...state,
    updateSubscriptionProcessing: true,
    updateSubscriptionSucceed: initialState.updateSubscriptionSucceed,
    updateSubscriptionFailed: initialState.updateSubscriptionFailed,
    updateSubscriptionError: initialState.updateSubscriptionError,
  })),
  on(SubscriptionActions.updateSubscriptionSuccess, (state) => ({
    ...state,
    updateSubscriptionProcessing: false,
    updateSubscriptionSucceed: true,
  })),
  on(SubscriptionActions.updateSubscriptionFailure, (state, { error }) => ({
    ...state,
    signupProcessing: false,
    updateSubscriptionProcessing: false,
    updateSubscriptionFailed: true,
    updateSubscriptionError: error,
    signupError: error,
  })),
  // Payemnt Failed
  on(PaymentActions.processPaypalPaymentFailure, PaymentActions.processStripePaymentFailure, (state, { error }) => ({
    ...state,
    signupProcessing: false,
    signupFailed: true,
    signupSucceed: false,
  })),
  // Update Form Data
  on(SubscriptionActions.updateFormData, (state, { data }) => ({
    ...state,
    formData: data,
  })),
  // Plans
  on(SubscriptionActions.loadPlans, (state) => ({
    ...state,
    plans: initialState.plans,
    plansLoading: true,
  })),
  on(SubscriptionActions.loadPlansSuccess, (state, { plans }) => ({
    ...state,
    plans,
    plansLoading: false,
    loadPlansSuccess: true,
    planGroupLoaded: true,
  })),
  on(SubscriptionActions.loadPlansFailure, (state, { error }) => ({
    ...state,
    plansLoading: false,
    plansError: error
  })),
  // Compute Plan
  on(SubscriptionActions.computePlan, (state) => ({
    ...state,
    computedPlan: initialState.computedPlan,
    planPriceComputingError: initialState.planPriceComputingError,
    planPriceComputing: true,
  })),
  on(SubscriptionActions.computePlanSuccess, (state, { computedPlan }) => ({
    ...state,
    computedPlan,
    planPriceComputing: false,
  })),
  on(SubscriptionActions.computePlanFailure, (state, { error }) => ({
    ...state,
    planPriceComputingError: error,
    planPriceComputing: false,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return signupReducer(state, action);
}

export const getPlans = (state: State) => state.plans;
export const getComputedPlan = (state: State) => state.computedPlan;
export const getFormData = (state: State) => state.formData;
export const getUpdateAccountProcessing = (state: State) => state.updateAccountProcessing;
export const getUpdateAccountSucceed = (state: State) => state.updateAccountSucceed;
export const getUpdateAccountFailed = (state: State) => state.updateAccountFailed;
export const getUpdateBillingInformationsProcessing = (state: State) => state.updateBillingInformationsProcessing;
export const getUpdateBillingInformationsSucceed = (state: State) => state.updateBillingInformationsSucceed;
export const getUpdateBillingInformationsFailed = (state: State) => state.updateBillingInformationsFailed;
export const getUpdateBillingInformationsError = (state: State) => state.updateBillingInformationsError;
export const getUpdateSubscriptionProcessing = (state: State) => state.updateSubscriptionProcessing;
export const getUpdateSubscriptionSucceed = (state: State) => state.updateSubscriptionSucceed;
export const getUpdateSubscriptionFailed = (state: State) => state.updateSubscriptionFailed;
export const getSignupProcessing = (state: State) => state.signupProcessing;
export const getSignupSucceed = (state: State) => state.signupSucceed;
export const getSignupFailed = (state: State) => state.signupFailed;
export const getSignupError = (state: State) => state.signupError;
export const getPlanGroupLoaded = (state: State) => state.planGroupLoaded;

